import React, { type ReactElement, type ReactNode, useId } from "react";
import {
  StyleSheet,
  View,
  Platform,
  type AccessibilityState,
  type AccessibilityRole,
} from "react-native";

import { ErrorText } from "../ErrorText";
import { Label } from "../Label";
import { useTheme } from "../ThemeProvider";
import type { StrictPaddingStyle } from "../util/types";
import { SelectButton } from "./SelectButton";

export interface accessibilityProps {
  /**
   * iOS Only: Label of the field. Also used to denote required and invalid states
   */
  accessibilityLabel?: string;

  /**
   * Android Only: Element ID to the label of the field. Also used to denote required and invalid states
   */
  accessibilityLabelledBy?: string;

  /**
   * Native Only: Auxiliary text. Used in error explanation as well
   */
  accessibilityHint?: string;

  /**
   * Accessibility Role tells a person the type of element that is focused on.
   */
  accessibilityRole?: AccessibilityRole;

  /**
   * Accessibility state for the select input
   */
  accessibilityState?: AccessibilityState;

  /**
   * Web Only: sets required state for screen reader users
   */
  "aria-required"?: boolean;

  /**
   * Web Only: sets invalid state for screen reader users
   */
  "aria-invalid"?: boolean;

  /**
   * Web Only: Auxiliary text. Used in error explanation as well
   */
  "aria-describedby"?: string;

  /**
   * Web Only: Element ID to the label of the field
   */
  "aria-labelledby"?: string;

  /**
   * Web Only: Element ID to the error message of the field
   */
  "aria-errormessage"?: string;

  /**
   * Web Only: Accessibility expanded state for the select input
   */
  "aria-expanded"?: boolean;

  /**
   * Web Only: ID of the element controlled by this input (e.g. the listbox/dialog)
   */
  "aria-controls"?: string;
}

export interface SelectProps {
  /**
   * The text shown in the select button
   */
  text?: string;
  /**
   * Disables the control.
   */
  disabled?: boolean;
  /**
   * The name of the field as shown in the title the dropdown modal
   */
  labelText?: string;
  /**
   * Whether the label is required (displays an asterisk)
   */
  required?: boolean;
  /**
   * Accessibility text to denote the field is required to screenreaders
   * Native iOS and Android do not have a concept of `required` like web
   * This should be set to the translated text of the word "Required"
   * If requiredText is not specified, the required text will default to English
   */
  requiredText?: string;
  /**
   * the icon displayed before the label (optional).
   */
  icon?: ReactNode;
  /**
   * Helper text shown below the label
   */
  explainerText?: string;
  /**
   * Error text shown when invalid is true
   */
  errorText?: string;
  /**
   * Accessibility text to denote the field is an invalid state to screenreaders
   * Native iOS and Android do not have a concept of `invalid` like web
   * This should be set to the translated text of the word "Invalid"
   * If invalidText is not specified, the invalid text will default to English
   */
  invalidText?: string;
  /**
   * The placeholder for the select button
   */
  placeholderText?: string;
  /**
   * When true, the selected value is constrained to a single line and
   * ellipsizes instead of pushing the chevron out of the field. Defaults to
   * false to preserve the existing layout for current consumers. See RN-9687.
   */
  truncateLabel?: boolean;
  /**
   * When the button is pressed
   */
  onPress: () => void;

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;

  /**
   * Accessibility expanded state for the select input
   */
  expanded?: boolean;

  /**
   * Accessible name/short description of combobox
   * @default "combobox"
   */
  description?: string;

  /**
   * Web Only: ID of the element controlled by this input (e.g. the listbox/dialog)
   */
  ariaControls?: string;

  /**
   * Props passed down to the SelectButton component
   */
  SelectButtonProps?: {
    ref?: React.Ref<View>;
    sx?: StrictPaddingStyle;
  };
}

/**
 * Mobile App Design Library - Select Input
 *
 * @see https://www.figma.com/design/UQ0baUglOtMCWJ1jkwcSzW/Mobile-Apps-Design-System--MADS-?m=auto&node-id=620-7191
 */
export function SelectInput({
  text,
  disabled = false,
  required = false,
  requiredText,
  icon,
  explainerText,
  errorText,
  invalidText,
  labelText,
  placeholderText,
  truncateLabel = false,
  onPress,
  sx,
  expanded = false,
  description = "combobox",
  ariaControls,
  SelectButtonProps,
}: SelectProps): ReactElement {
  const theme = useTheme();

  const styles = StyleSheet.create({
    container: {
      gap: theme.spacing.s1,
    },
    disabled: {
      opacity: 0.4,
    },
  });

  const labelId = `MADS_SelectInput_label_${useId()}`;
  const explainerTextId = `MADS_SelectInput_explainerText_${useId()}`;
  const errorTextId = `MADS_SelectInput_errorText_${useId()}`;

  // Platform detection for conditional attribute rendering
  const is_iOS = Platform.OS === "ios";
  const is_android = Platform.OS === "android";
  const is_native_mobile = is_android || is_iOS;

  const invalid = errorText !== undefined && errorText !== "";

  const a11yProps = React.useMemo<accessibilityProps>(() => {
    // Role is `button` (not `combobox`): RN Highlander only allows
    // button/link/imagebutton/radio/tab for pressables. `disabled` in
    // accessibilityState exposes the disabled state; `aria-expanded` conveys open/closed.
    const props: accessibilityProps = {
      accessibilityState: { disabled, expanded },
      accessibilityRole: "button",
      "aria-expanded": expanded,
    };

    /*
     * A11y Inclusions
     * These are broken up into the accessible purpose so that they may be retired as the technology catches up
     * Ex. There currently isn't a iOS & Android required attribute, but that will likely change in future versions
     *
     * iOS & Android: Required and Invalid states need to be in the `accessibilityLabel`
     * iOS & Android: Explainer/Helper Text and Error Text need to be in the `accessibilityHint`
     */

    // Accessible Label
    if (is_native_mobile) {
      let label = labelText ?? "";
      // Include selected option text in the announcement
      if (text) {
        label = label ? `${label}, ${text}` : text;
      }
      props.accessibilityLabel = label;
    } else if (labelText) {
      props["aria-labelledby"] = labelId;
    }

    // Accessible Helper Text
    if (explainerText) {
      if (is_native_mobile) {
        props.accessibilityHint = explainerText.trim();
      } else {
        props["aria-describedby"] = explainerTextId;
      }
    }

    // Accessible "required" annotations
    if (required) {
      if (is_native_mobile) {
        props.accessibilityLabel = `${props.accessibilityLabel}. ${requiredText ? requiredText : "required"}`;
      } else {
        props["aria-required"] = true;
      }
    }

    // Accessible "invalid" annotations
    if (invalid) {
      if (is_native_mobile) {
        props.accessibilityLabel = `${props.accessibilityLabel}. ${invalidText ? invalidText : "invalid"}`;
      } else {
        props["aria-invalid"] = true;
      }
      if (is_native_mobile) {
        // Error text comes first (matching TextInput pattern), then helper text
        props.accessibilityHint = `${errorText} ${props.accessibilityHint || ""}`.trim();
      } else {
        // For web, errorTextId comes first in aria-describedby (matching TextInput pattern)
        props["aria-describedby"] = explainerText
          ? `${errorTextId} ${explainerTextId}`.trim()
          : errorTextId;
      }
    }

    // iOS: AccessibilityRole is not supported, we need to add the description to the accessibilityLabel
    if (is_iOS) {
      props.accessibilityLabel = `${props.accessibilityLabel}. ${description}`;
    }

    if (ariaControls && !is_native_mobile) {
      props["aria-controls"] = ariaControls;
    }

    if (errorText) {
      props["aria-errormessage"] = errorTextId;
    }

    return props;
  }, [
    disabled,
    expanded,
    is_native_mobile,
    labelText,
    explainerText,
    required,
    invalid,
    is_iOS,
    text,
    labelId,
    explainerTextId,
    requiredText,
    invalidText,
    errorText,
    errorTextId,
    description,
    ariaControls,
  ]);

  return (
    <View
      testID="select-input-container"
      style={[styles.container, disabled && styles.disabled, sx]}
    >
      {labelText && (
        <Label
          text={labelText}
          labelID={labelId}
          explainerText={explainerText}
          explainerTextID={explainerTextId}
          isRequired={required}
          requiredText={requiredText}
          isInvalid={invalid}
          invalidText={invalidText}
          icon={icon}
          // A11Y: Android requires states to be in the label so it may be used by accessibilityLabelledby
          renderStatesToScreenReader={is_android}
        />
      )}
      <SelectButton
        ref={SelectButtonProps?.ref}
        label={text}
        onPress={onPress}
        invalid={invalid}
        disabled={disabled}
        placeholder={placeholderText}
        truncateLabel={truncateLabel}
        sx={SelectButtonProps?.sx}
        {...a11yProps}
      />
      {errorText && <ErrorText nativeID={errorTextId}>{errorText}</ErrorText>}
    </View>
  );
}
